import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, AlertTriangle, RotateCcw, Trash2 } from 'lucide-react';
import { fmtDate } from './issueMeta';
import ProposalCard from './ProposalCard';
import { apiErrorText } from '@/lib/vaultUpload';

const API = process.env.REACT_APP_BACKEND_URL;
const GROUPS = [
  { kind: 'uncertain', title: 'Needs your decision', hint: 'The AI is not sure whether these continue an existing issue.' },
  { kind: 'update', title: 'Updates to existing issues', hint: '' },
  { kind: 'new', title: 'New issues', hint: '' },
  { kind: 'not_mentioned', title: 'Not mentioned in these minutes', hint: 'These stay open and will show “No update in latest minutes”. Reject one only if the issue was in fact discussed.' },
];

export default function MinutesReviewDialog({ projectId, open, onOpenChange, headers, onPublished }) {
  const [reviews, setReviews] = useState([]);
  const [rec, setRec] = useState(null);
  const [meetingDate, setMeetingDate] = useState('');
  const [busy, setBusy] = useState(false);

  const loadList = () => axios.get(`${API}/api/post-oc/${projectId}/reviews`, { headers })
    .then((r) => {
      const pend = r.data.reviews.filter((x) => ['processing', 'ready', 'failed'].includes(x.state));
      setReviews(pend);
      if (pend[0]) openRec(pend[0].id); else setRec(null);
    });
  const openRec = (id) => axios.get(`${API}/api/post-oc/reviews/${id}`, { headers }).then((r) => {
    setRec(r.data); setMeetingDate(r.data.meeting_date || '');
  });

  useEffect(() => { if (open) { setRec(null); loadList(); } }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!open || rec?.state !== 'processing') return undefined;
    const t = setInterval(() => openRec(rec.id), 5000);
    return () => clearInterval(t);
  }, [open, rec?.state, rec?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const patch = async (pid, body) => {
    try {
      const r = await axios.patch(`${API}/api/post-oc/reviews/${rec.id}/proposals/${pid}`, body, { headers });
      setRec((cur) => ({ ...cur, proposals: cur.proposals.map((p) => (p.id === pid ? r.data : p)) }));
    } catch (e) { toast.error(apiErrorText(e, 'Could not save')); }
  };
  const act = async (path, body, msg) => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/api/post-oc/reviews/${rec.id}/${path}`, body || {}, { headers });
      if (msg) toast.success(typeof msg === 'function' ? msg(r.data) : msg);
      onPublished?.();
      setRec(null); loadList();
    } catch (e) { toast.error(apiErrorText(e, 'Action failed')); } finally { setBusy(false); }
  };

  const undecided = rec?.proposals?.filter((p) => p.kind === 'uncertain' && p.decision === 'pending').length || 0;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl" data-testid="minutes-review-dialog">
        <DialogHeader>
          <DialogTitle>Review minutes before publishing</DialogTitle>
          <DialogDescription>Check what the AI read from the minutes. Nothing is shown to members until you publish.</DialogDescription>
        </DialogHeader>
        {reviews.length > 1 && (
          <div className="flex gap-2 flex-wrap" data-testid="minutes-review-list">
            {reviews.map((r) => (
              <Button key={r.id} size="sm" variant={rec?.id === r.id ? 'default' : 'outline'} onClick={() => openRec(r.id)} data-testid={`minutes-review-tab-${r.id}`}>
                {r.meeting_date ? fmtDate(r.meeting_date) : r.doc_title}
              </Button>
            ))}
          </div>
        )}
        {!rec && !reviews.length && <p className="text-sm text-muted-foreground py-6 text-center" data-testid="minutes-review-empty">Nothing waiting for review.</p>}
        {rec?.state === 'processing' && (
          <div className="py-8 text-center text-sm text-muted-foreground" data-testid="minutes-review-processing">
            <Loader2 className="h-5 w-5 animate-spin mx-auto mb-2" />Reading “{rec.doc_title}” and comparing it with earlier meetings. This takes about a minute.
          </div>
        )}
        {rec?.state === 'failed' && (
          <div className="rounded-md border border-red-200 bg-red-50 dark:bg-red-900/20 p-3 text-sm" data-testid="minutes-review-failed">
            The minutes could not be read automatically ({rec.error}). The file is saved in the vault. Try again, or discard.
          </div>
        )}
        {rec?.state === 'ready' && (
          <div className="space-y-4">
            <div className="rounded-md bg-muted/50 p-3 text-sm" data-testid="minutes-review-summary">
              <p className="font-medium">{rec.meeting_title}</p>
              <p className="text-muted-foreground mt-1">{rec.summary}</p>
            </div>
            {rec.warnings?.map((w) => (
              <p key={w} className="flex gap-2 text-xs text-amber-800 dark:text-amber-300"><AlertTriangle className="h-3.5 w-3.5 shrink-0" />{w}</p>
            ))}
            {GROUPS.map((g) => {
              const list = rec.proposals.filter((p) => p.kind === g.kind);
              if (!list.length) return null;
              return (
                <section key={g.kind} data-testid={`review-group-${g.kind}`}>
                  <h4 className="text-xs font-semibold uppercase tracking-wide">{g.title} ({list.length})</h4>
                  {g.hint && <p className="text-xs text-muted-foreground mb-2">{g.hint}</p>}
                  <div className="space-y-2 mt-2">
                    {list.map((p) => <ProposalCard key={p.id} p={p} openIssues={rec.open_issues} onPatch={patch} />)}
                  </div>
                </section>
              );
            })}
          </div>
        )}
        {rec && rec.state !== 'processing' && (
          <DialogFooter className="items-center gap-2">
            {rec.state === 'ready' && (
              <div className="flex items-center gap-2 mr-auto">
                <Label className="text-xs whitespace-nowrap">Meeting date</Label>
                <Input type="date" value={meetingDate} onChange={(e) => setMeetingDate(e.target.value)} className="h-8 w-40" data-testid="minutes-review-date" />
              </div>
            )}
            <Button variant="ghost" size="sm" disabled={busy} onClick={() => act('discard', null, 'Discarded')} data-testid="minutes-review-discard"><Trash2 className="h-4 w-4 mr-1" />Discard</Button>
            <Button variant="outline" size="sm" disabled={busy} onClick={() => act('retry', null, 'Reading again…')} data-testid="minutes-review-retry"><RotateCcw className="h-4 w-4 mr-1" />Read again</Button>
            {rec.state === 'ready' && (
              <Button size="sm" disabled={busy || undecided > 0 || !meetingDate} onClick={() => act('approve', { meeting_date: meetingDate },
                (d) => `Published: ${d.result.new} new, ${d.result.updated} updated, ${d.result.no_update} with no update`)} data-testid="minutes-review-publish">
                {busy && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
                {undecided ? `Decide ${undecided} item(s) first` : 'Publish the rest'}
              </Button>
            )}
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
